import {
  useRedirectPrompt,
  useRouteSummary
} from '$contexts/RouteContext/hooks'
import { useAppUserService } from '$hooks/services'
import { zodResolver } from '@hookform/resolvers/zod'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useCallback, useEffect } from 'react'
import AssignRoles from '../../blocks/AssignRoles'
import FooterAction from '$components/FooterAction'
import { Button, Spinner } from '@genie-fintech/ui/components'
import { z } from 'zod'
import { container, footer } from './styles.css'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { ChevronLeft } from 'lucide-react'
import { useAssignRoleStore } from '$hooks/stores'
import { TRoles } from '$services/types'
import usePageClass from '$components/Modal/hooks/usePageClass'
import { useIsMounted } from '@genie-fintech/ui/hooks'
import { trueOrUndefined } from '@genie-fintech/ui/functions'

const { colors } = themeVars

const schema = z.object({
  roles: z.number().array().min(1, 'Please assign one role at least!')
})

type TFormValues = z.infer<typeof schema>

const DEFAULT_FORM_VALUES: TFormValues = {
  roles: []
}

const AppUserAssignRoles = () => {
  const addRoles = useAssignRoleStore(r => r.addRoles)
  const setSelectedRoles = useAssignRoleStore(r => r.setSelectedRoles)

  const isMounted = useIsMounted()

  const { route } = useRouteSummary()

  const { appId, userId } = route.queryParams

  usePageClass({ prefix: 'background-low', name: 'app-user-assign-roles-page' })

  const {
    fetchAppUserDetailAsync,
    attachRoleToUserAsync,
    attachingRoleToUser
  } = useAppUserService()

  const methods = useForm<TFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors }
  } = methods

  const { replace } = useFieldArray<TFormValues>({
    name: 'roles' as never,
    control
  })

  const selectedRoles = useWatch({ name: 'roles', control })

  useRedirectPrompt({ shouldPrompt: isDirty })

  useEffect(() => {
    if (!appId || !userId) return
    fetchAppUserDetailAsync({
      application_id: appId,
      user_id: userId
    }).then(({ data }) => {
      const modifiedData: TFormValues = { roles: data.roles.map(v => v.id) }

      const selectedRoles = data.roles.map(r => ({
        ...r,
        isDisabled: true
      })) as TRoles[]

      addRoles(selectedRoles)
      setSelectedRoles(selectedRoles)
      reset(modifiedData)
    })
  }, [
    appId,
    userId,
    fetchAppUserDetailAsync,
    reset,
    addRoles,
    setSelectedRoles
  ])

  const onSubmit = handleSubmit(({ roles }: TFormValues) => {
    if (!appId || !userId) return
    return attachRoleToUserAsync(appId, userId, roles).then(() => {
      history.back()
    })
  })

  const onBack = useCallback(() => {
    history.back()
  }, [])

  return (
    <form
      className={container}
      onSubmit={onSubmit}
      data-ready={trueOrUndefined(isMounted)}
    >
      <article>
        <Button
          styleVariants={{
            kind: 'neutral',
            type: 'outlined',
            size: 'small'
          }}
          style={{ background: colors.area.high }}
          onClick={onBack}
          disabled={attachingRoleToUser}
        >
          <ChevronLeft size={16} />
          Back
        </Button>
      </article>

      <AssignRoles
        roles={selectedRoles}
        onChange={replace}
        errorMessage={errors.roles?.message}
        appId={appId}
      />

      <FooterAction>
        <article className={footer}>
          <Button type="submit" disabled={!isDirty || attachingRoleToUser}>
            {attachingRoleToUser && <Spinner />}
            Save Changes
          </Button>
        </article>
      </FooterAction>
    </form>
  )
}

export default AppUserAssignRoles
